import type { WtUnit } from "@/db/schema/weight-training";

export type WeightTrainingWorkoutStyle =
  | "strength"
  | "hypertrophy"
  | "conditioning"
  | "mixed";

export type WeightTrainingWorkoutPostPayload = {
  sourceType: "weight_training_workout";
  workoutId: string;
  workoutName: string;
  style: WeightTrainingWorkoutStyle;
  startedAt: string;
  durationMins: number;
  durationSeconds: number;
  totalVolume: number;
  totalSets: number;
  totalReps: number;
  prsAchieved: number;
  exerciseCount: number;
  weightUnit: WtUnit;
  shareVariant: "sticker";
};

export function deriveWeightTrainingWorkoutStyle(
  completedSetReps: number[],
): WeightTrainingWorkoutStyle {
  const validReps = completedSetReps.filter(
    (reps) => Number.isFinite(reps) && reps > 0,
  );

  if (validReps.length === 0) {
    return "mixed";
  }

  const averageReps =
    validReps.reduce((sum, reps) => sum + reps, 0) / validReps.length;

  if (averageReps <= 6) {
    return "strength";
  }

  if (averageReps >= 7 && averageReps <= 12) {
    return "hypertrophy";
  }

  if (averageReps >= 15) {
    return "conditioning";
  }

  return "mixed";
}

export function buildWeightTrainingWorkoutPostPayload(input: {
  workoutId: string;
  workoutName?: string | null;
  style: WeightTrainingWorkoutStyle;
  startedAt: string;
  durationSeconds?: number | null;
  totalVolume?: number | null;
  totalSets?: number | null;
  totalReps?: number | null;
  prsAchieved?: number | null;
  exerciseCount?: number | null;
  weightUnit: WtUnit;
  shareVariant?: "sticker" | null;
}): WeightTrainingWorkoutPostPayload {
  const safeDurationSeconds = Math.max(
    0,
    Math.floor(input.durationSeconds ?? 0),
  );

  return {
    sourceType: "weight_training_workout",
    workoutId: input.workoutId,
    workoutName: input.workoutName?.trim() || "Workout",
    style: input.style,
    startedAt: input.startedAt,
    durationMins: Math.max(0, Math.round(safeDurationSeconds / 60)),
    durationSeconds: safeDurationSeconds,
    totalVolume: Math.max(0, input.totalVolume ?? 0),
    totalSets: Math.max(0, Math.floor(input.totalSets ?? 0)),
    totalReps: Math.max(0, Math.floor(input.totalReps ?? 0)),
    prsAchieved: Math.max(0, Math.floor(input.prsAchieved ?? 0)),
    exerciseCount: Math.max(0, Math.floor(input.exerciseCount ?? 0)),
    weightUnit: input.weightUnit,
    shareVariant: input.shareVariant ?? "sticker",
  };
}

export function isWeightTrainingWorkoutPostPayload(
  value: unknown,
): value is WeightTrainingWorkoutPostPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    record.sourceType === "weight_training_workout" &&
    typeof record.workoutId === "string" &&
    typeof record.workoutName === "string" &&
    typeof record.startedAt === "string" &&
    typeof record.durationMins === "number" &&
    Number.isFinite(record.durationMins) &&
    typeof record.durationSeconds === "number" &&
    Number.isFinite(record.durationSeconds) &&
    typeof record.totalVolume === "number" &&
    Number.isFinite(record.totalVolume) &&
    typeof record.totalSets === "number" &&
    Number.isFinite(record.totalSets) &&
    typeof record.totalReps === "number" &&
    Number.isFinite(record.totalReps) &&
    typeof record.prsAchieved === "number" &&
    Number.isFinite(record.prsAchieved) &&
    typeof record.exerciseCount === "number" &&
    Number.isFinite(record.exerciseCount) &&
    (record.style === "strength" ||
      record.style === "hypertrophy" ||
      record.style === "conditioning" ||
      record.style === "mixed") &&
    (record.weightUnit === "kg" || record.weightUnit === "lb") &&
    record.shareVariant === "sticker"
  );
}
